/* Select primitive, wrapping native <select>.

   Native keeps keyboard behaviour, mobile's platform picker and accessibility for
   free — a custom listbox would have to earn its way in, and nothing here needs
   one. Only the chrome is restyled.

   Emits `g-change` with the selected value. */

import { LitElement, html, css } from 'lit';
import { shadowReset } from '../../design/base.js';
import { customElement, property, query } from 'lit/decorators.js';

export interface SelectOption {
  value: string;
  label: string;
}

@customElement('g-select')
export class GSelect extends LitElement {
  static override styles = [
    shadowReset,
    css`
      :host {
        display: block;
      }
      label {
        display: block;
        margin-bottom: var(--space-1);
        color: var(--text-dim);
        font-size: 0.78rem;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }
      select {
        width: 100%;
        height: 34px;
        padding: 0 var(--space-3);
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        background: var(--bg);
        color: var(--text);
        font: inherit;
        font-family: var(--font-ui);
      }
      select:focus-visible {
        border-color: var(--accent);
        box-shadow: var(--ring);
        outline: none;
      }
    `,
  ];

  @property({ attribute: false }) options: SelectOption[] = [];
  @property() value = '';
  @property() label = '';

  @query('select') private el?: HTMLSelectElement;

  /**
   * Apply `value` AFTER the options exist.
   *
   * Setting `select.value` while the element still has no matching <option> is
   * silently ignored, and the control then displays the first option instead —
   * which for a destructive confirm means showing a different target than the one
   * that would actually be used. Syncing here keeps display and state in step.
   */
  override updated(): void {
    if (this.el && this.el.value !== this.value) this.el.value = this.value;
  }

  override render() {
    return html`
      ${this.label ? html`<label for="s">${this.label}</label>` : ''}
      <select
        id="s"
        aria-label=${this.label || 'Select'}
        @change=${(e: Event) => {
          this.value = (e.target as HTMLSelectElement).value;
          this.dispatchEvent(
            new CustomEvent('g-change', { detail: this.value, bubbles: true, composed: true }),
          );
        }}
      >
        ${this.options.map((o) => html`<option value=${o.value}>${o.label}</option>`)}
      </select>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-select': GSelect;
  }
}
